import {
  all,
  fork,
  put,
  select,
  take,
  takeEvery,
  takeLatest,
} from 'redux-saga/effects';
import config from '../../../config';

import { history } from '../../../core';
import { GlobalActionConstants } from '../../../core/types/redux';
import { performApiPost } from '../../../core/utils/sagaUtils';
import { ApplicationState } from '../../types';
import { setLogin } from '../login/actions';
import { LoginInfo } from '../login/types';
import {
  closeSettingsPopup,
  openMessageBox,
  openSnackBar,
  setUpdatedPassword,
  setUpdatedProfile,
} from './actions';
import {
  MainActionConstants,
  NavigateArgs,
  MainAction,
  ProfileResult,
  UserProfile,
  RequestUpdateProfileArgs,
  RequestUpdatePasswordArgs,
  SetUpdatedProfileArgs,
} from './types';

function* navigateToPageFlow() {
  while (true) {
    const act = (
      yield take(MainActionConstants.NAVIGATE)
    ) as MainAction<NavigateArgs>;

    const { page, params, target } = act;
    const searchParams = new URLSearchParams(params);
    const url = `${config.routerBase}/${page}?${searchParams.toString()}`;
    if (target) {
      if (!window.childrenLookup) {
        window.childrenLookup = {};
      }

      const childWindow = window.childrenLookup[target];
      if (childWindow && !childWindow.closed) {
        childWindow.focus();
      } else {
        window.childrenLookup[target] = window.open(url, target);
      }
    } else {
      history.push(url);
    }
  }
}

function* httpUnauthorizedFlow() {
  yield takeLatest(
    GlobalActionConstants.HTTP_UNAUTHORIZED_NOTIFY,
    function* _() {
      yield put(openMessageBox({
        title: 'Session Expired',
        content: 'Your login session expired. Please login again.',
        blurBackdrop: true,
        dismissText: 'OK',
        onDismiss: () => {
          const loginUrl = `${config.routerBase}/login`;
          window.location.href = `${loginUrl}`;
        },
      }));
    },
  );
}

function* updateProfileFlow() {
  yield takeEvery(MainActionConstants.PROFILE_UPDATE,
    function* _(act: MainAction<RequestUpdateProfileArgs>) {
      yield fork((
        payload,
      ) => performApiPost<ProfileResult>(
        'v1/User/UpdateProfile', {
          payload,
          onResult: ({ result, error }) => put(setUpdatedProfile({ result, error })),
        },
      ),
      act);
    });
}

function* updatedProfileFlow() {
  yield takeEvery(MainActionConstants.PROFILE_UPDATED_SET, function* _() {
    const userProfile: UserProfile = yield select(
      (state: ApplicationState) => state.main.settingsPopup.userProfile,
    );

    const loginStatus: LoginInfo = yield select(
      (state: ApplicationState) => state.login.loginStatus,
    );

    yield put(setLogin({
      result: {
        ...loginStatus,
        data: {
          ...loginStatus.data,
          userPersonName: userProfile.userPersonName,
          userPosition: userProfile.userPosition,
        },
      },
    }));

    yield put(closeSettingsPopup());
  });
}

function* updatePasswordFlow() {
  yield takeEvery(MainActionConstants.PASSWORD_UPDATE,
    function* _(act: MainAction<RequestUpdatePasswordArgs>) {
      yield fork((
        payload,
      ) => performApiPost<ProfileResult>(
        'v1/User/UpdatePassword', {
          payload,
          onResult: ({ result, error }) => put(setUpdatedPassword({ result, error })),
        },
      ),
      act);
    });
}

function* updatedPasswordFlow() {
  yield takeEvery(MainActionConstants.PASSWORD_UPDATED_SET,
    function* _(act: MainAction<SetUpdatedProfileArgs>) {
      if (act.result?.isSuccess) {
        yield put(closeSettingsPopup());
        yield put(openSnackBar({
          severity: 'success',
          messageSnackbar: act.result?.message,
        }));
      } else {
        yield put(openSnackBar({
          severity: 'error',
          messageSnackbar: act.result?.message,
        }));
      }
    });
}

export default function* mainSaga() {
  yield all([
    navigateToPageFlow(),
    httpUnauthorizedFlow(),
    updateProfileFlow(),
    updatedProfileFlow(),
    updatePasswordFlow(),
    updatedPasswordFlow(),
  ]);
}
